import React, { createContext, useContext, useState, ReactNode } from 'react';

interface GenericModalContextType {
  isOpen: boolean;
  title: string;
  modalContent?: ReactNode;
  openModal: (
    title?: string,
    content?: ReactNode,
    onConfirm?: () => void,
    onCancel?: () => void
  ) => void;
  closeModal: () => void;
  setTitle: (title: string) => void;            // add this setter
  setContent: (content: ReactNode) => void;     // add this setter
  confirm: () => void;
  cancel: () => void;
}

const GenericModalContext = createContext<GenericModalContextType | undefined>(undefined);

export const GenericModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, _setTitle] = useState('bazar elkher');
  const [modalContent, _setModalContent] = useState<ReactNode>(null);
  const [onConfirm, setOnConfirm] = useState<(() => void) | undefined>(undefined);
  const [onCancel, setOnCancel] = useState<(() => void) | undefined>(undefined);

  const openModal = (
    modalTitle?: string,
    content?: ReactNode,
    confirmCallback?: () => void,
    cancelCallback?: () => void
  ) => {
    _setTitle(modalTitle || 'bazar elkher');
    _setModalContent(content || null);
    setOnConfirm(() => confirmCallback);
    setOnCancel(() => cancelCallback);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    _setModalContent(null);
    setOnConfirm(undefined);
    setOnCancel(undefined);
  };

  const confirm = () => {
    if (onConfirm) onConfirm();
    closeModal();
  };

  const cancel = () => {
    if (onCancel) onCancel();
    closeModal();
  };

  // Expose setTitle and setContent setters for dynamic updates
  const setTitle = (newTitle: string) => {
    _setTitle(newTitle);
  };

  const setContent = (newContent: ReactNode) => {
    _setModalContent(newContent);
  };

  return (
    <GenericModalContext.Provider
      value={{
        isOpen,
        title,
        modalContent,
        openModal,
        closeModal,
        confirm,
        cancel,
        setTitle,
        setContent,
      }}
    >
      {children}
    </GenericModalContext.Provider>
  );
};

export const useGenericModal = () => {
  const context = useContext(GenericModalContext);
  if (!context) {
    throw new Error('useGenericModal must be used within a GenericModalProvider');
  }
  return context;
};
